import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { FormatSettings } from '@progress/kendo-angular-dateinputs';
import { PageChangeEvent } from '@progress/kendo-angular-grid';
import { BehaviorSubject, combineLatest, map, startWith, Subject, tap } from 'rxjs';
import { IBaseSearchRequest, IGridState, PageSize } from 'src/app/core/models/base';
import { AppStateInterface } from 'src/app/stores/global/appState.interface';
import { createFiscalYear, fiscalYearsSelector, getFiscalYears, lockFiscalYear, unlockFiscalYear, updateFiscalYear } from 'src/app/stores/global/fiscal-years/fiscal-years.store';
import { ICreateFiscalYearRequest, IFiscalYear, IUpdateFiscalYearRequest } from '../../models/fiscal-year';
import { cancelIcon, lockIcon, pencilIcon, saveIcon, trashIcon, unlockIcon } from '@progress/kendo-svg-icons';

@Component({
    selector: 'app-fiscal-year-grid',
    templateUrl: './fiscal-year-grid.component.html',
    styleUrls: ['./fiscal-year-grid.component.css'],
    standalone: false
})
export class FiscalYearGridComponent implements OnInit {

  constructor(
    private store: Store<AppStateInterface>
  ) {
    this.store.dispatch(getFiscalYears())
  }

  get pageSizes(){
    return PageSize;
  }

  public format: FormatSettings = {
    displayFormat: "MMM yyyy",
    inputFormat: "MMM yyyy",
  };

  private editedRowIndex: number;
  public formGroup: UntypedFormGroup;

  //icons
  public icons  = {
    unlock: unlockIcon,
    lock: lockIcon,
    cancel: cancelIcon,
    save: saveIcon,
    delete: trashIcon,
    edit: pencilIcon,
  };

  gridState$ = new BehaviorSubject<IGridState>(
    {
      pageSize: 10,
      skip: 0,
    }
  )

  searchRequest$ = new BehaviorSubject<IBaseSearchRequest>({
    pageNumber: 1,
    pageSize: this.pageSizes.Small,
  });

  pageChangeSub$ = new Subject<PageChangeEvent>();
  refreshSub$ = new Subject<boolean>();
  createItemSub$ = new Subject<ICreateFiscalYearRequest>();
  updateItemSub$ = new Subject<IUpdateFiscalYearRequest>();
  deleteItemSub$ = new Subject<string>();
  lockItemSub$ = new Subject<string>();
  unlockItemSub$ = new Subject<string>();

  vm$ = combineLatest([
    this.store.pipe(select(fiscalYearsSelector)),
    this.createItemSub$.pipe(
      tap(request => this.store.dispatch(createFiscalYear({request}))),
      startWith(null),
    ),
    this.updateItemSub$.pipe(
      tap(request => this.store.dispatch(updateFiscalYear({request}))),
      startWith(null),
    ),
    this.lockItemSub$.pipe(
      tap(id => this.store.dispatch(lockFiscalYear({request: {id}}))),
      startWith(null),
    ),
    this.unlockItemSub$.pipe(
      tap(id => this.store.dispatch(unlockFiscalYear({request: {id}}))),
      startWith(null),
    ),
  ]).pipe(
    map(([
      fiscalYears,
      createItemSub,
      updateItemSub,
      lockItemSub,
      unlockItemSub
    ]) => ({
      fiscalYears,
      createItemSub,
      updateItemSub,
      lockItemSub,
      unlockItemSub
    }))
  );

  ngOnInit(): void {
  }

  createFromGroup(fiscalYear : IFiscalYear = null){
    return new UntypedFormGroup({
      id: new UntypedFormControl(fiscalYear?.id ?? null),
      startDate: new UntypedFormControl(fiscalYear?.startDate ?? null, Validators.required),
      endDate: new UntypedFormControl(fiscalYear?.endDate ?? null, Validators.required),
      lockedOn: new UntypedFormControl(fiscalYear?.lockedOn ?? null),
      lockedById: new UntypedFormControl(fiscalYear?.lockedById ?? null),
    });
  }

  public cancelHandler({ sender, rowIndex }) {
    this.closeEditor(sender, rowIndex);
  }

  addHandler({ sender }) {
    this.closeEditor(sender);

    this.formGroup = this.createFromGroup();

    sender.addRow(this.formGroup);
  }

  editHandler({ sender, rowIndex, dataItem }) {
    this.closeEditor(sender);

    this.formGroup = this.createFromGroup(dataItem);

    this.editedRowIndex = rowIndex;

    sender.editRow(rowIndex, this.formGroup);
  }

  saveHandler(event){
    if(this.formGroup.value.id == null){
        this.createItemSub$.next({
        startDate: this.formatStartDate(this.formGroup.value.startDate),
        endDate: this.formatEndDate(this.formGroup.value.endDate),
      })
    } else {
      this.updateItemSub$.next({
        id: this.formGroup.value.id,
        startDate: this.formatStartDate(this.formGroup.value.startDate),
        endDate: this.formatEndDate(this.formGroup.value.endDate),
      })
    }

    this.closeEditor(event.sender);
  }

  // helper to make sure that no timezone switch happens
  private formatStartDate(date: Date | string): string {
    const d = date instanceof Date ? date : new Date(date);
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    return `${year}-${month}-01`;
  }

  private formatEndDate(date: Date | string): string {
    const d = date instanceof Date ? date : new Date(date);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const lastDay = new Date(year, month, 0).getDate(); // 0th day of next month = last day of current month
    return `${year}-${month.toString().padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`;
  }

  deletionDialogOpened = false;
  private itemToDelete: { id: string, sender: any } | null = null;

  removeHandler(event){
    this.itemToDelete = { id: event.dataItem.id, sender: event.sender };
    this.deletionDialogOpened = true;
  }

  deleteConfirmed(){
    const item = this.itemToDelete;
    this.itemToDelete = null;
    if (!item) return;
    this.deleteItemSub$.next(item.id)
    this.closeEditor(item.sender);
  }

  private closeEditor(grid, rowIndex = this.editedRowIndex) {
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    // this.showDialog$.next(false);
    this.formGroup = undefined;
  }

}
